import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import AppCard from './AppCard';
import { useAssistantV2Enabled } from '../../../modules/assistant/flag';

// Stream Chat sanitizes user IDs by lowercasing and replacing every char
// outside [a-zA-Z0-9@_-] with an underscore — dots in emails become
// underscores. Re-run the same transform so we can match the current user's
// stream ID back to their email key in forksByEmail.
const toStreamUserId = (email) =>
  (email || '').replace(/[^a-zA-Z0-9@_-]/g, '_').toLowerCase();

// Renders a shared AI chat session card in Stream Chat. Each recipient has
// their own fork — forksByEmail maps recipient email → fork threadId, so the
// "Open in AI Assistant" CTA navigates the current viewer to the copy owned
// by them (not to someone else's fork they can't read).
const AIChatShareMessage = ({ message }) => {
  const navigate = useNavigate();
  const assistantV2 = useAssistantV2Enabled();
  if (!message.attachments || message.attachments.length === 0) return null;

  const attachment = message.attachments.find(att => att.type === 'ai_session_share');
  if (!attachment) return null;

  const sessionInfo = attachment.sessionInfo || {};
  const forksByEmail = attachment.forksByEmail || {};
  const senderName = attachment.senderName || message.user?.name || 'A teammate';

  const currentStreamId = window.streamChatClient?.user?.id || null;
  const isCurrentUserSender = message.user?.id === currentStreamId;

  // Find the fork threadId owned by the current viewer. The sender sees the
  // original (they still have their own session open) so no CTA for them.
  let myForkThreadId = null;
  if (!isCurrentUserSender && currentStreamId) {
    const target = currentStreamId.toLowerCase();
    for (const [email, forkThreadId] of Object.entries(forksByEmail)) {
      if (toStreamUserId(email) === target) {
        myForkThreadId = forkThreadId;
        break;
      }
    }
  }

  const handleOpen = (event) => {
    event?.preventDefault?.();
    const threadId = isCurrentUserSender ? sessionInfo.originalThreadId : myForkThreadId;
    if (!threadId) {
      console.warn("[AIChatShareMessage] no copy of this shared chat for the current user");
      return;
    }
    // Assistant v2: the copy is a v1 conversation of the viewer's own, opened
    // through the old-link route, which imports it into v2.
    if (assistantV2) {
      navigate(`/ai-assistant/${encodeURIComponent(threadId)}`);
      return;
    }
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('lodgeic:assistant:open', {
        detail: { expanded: true, threadId }
      }));
    }, 150);
  };

  const messageCount = sessionInfo.messageCount || 0;
  const countLabel = messageCount > 0
    ? `${messageCount} message${messageCount !== 1 ? 's' : ''}`
    : 'AI conversation';
  const available = isCurrentUserSender || Boolean(myForkThreadId);

  return (
    <AppCard
      title={sessionInfo.hotelName ? `AI conversation · ${sessionInfo.hotelName}` : 'Shared AI conversation'}
      subtitle={countLabel}
      footNote={isCurrentUserSender ? 'Each person gets their own copy' : `Shared by ${senderName}`}
      action={available
        ? <>{isCurrentUserSender ? 'Open original' : 'Open in assistant'} <ArrowRight size={14} aria-hidden="true" /></>
        : 'Not available to you'}
      onOpen={handleOpen}
      disabled={!available}
    >
      {sessionInfo.lastMessage && (
        <div
          style={{
            padding: '0 12px 10px', fontSize: 12.5, lineHeight: 1.45, color: 'var(--lodge-onyx-600)',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}
        >
          {sessionInfo.lastMessage}
        </div>
      )}
    </AppCard>
  );
};

export default AIChatShareMessage;
